// Navigation destinations filtered by permission and capability, shared by the rail,
// the compact bottom bar and the More page.
import type { Session } from '@bcio-voice/contracts';
import type { ReactNode } from 'react';
import { Ban, BarChart3, Grid3x3, History, type LucideIcon, Menu, MessageSquare, Phone, Settings, Shield, UserRound, Users, Voicemail, Workflow } from 'lucide-react';
import { Icon } from '../ui/Icon.tsx';
import { Link, useRouter } from '../lib/router.tsx';
import { useWorkspace } from '../state/workspace.tsx';
import { capability, gate, hasPermission } from '../lib/capabilities.ts';
import { routingAccess } from '../features/routing/access.ts';
import { voicemailAccess } from '../features/voicemail/access.ts';
import { blocklistAccess } from '../features/blocklist/model.ts';
import { isOperatorMode, readinessTone } from '../features/phone/callView.ts';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  match: string[];
  /** Daily work sits in the index; workspace setup (admin, routing, phone menus) folds under Setup. */
  group?: 'setup';
}

export function navItems(session: Session, analyticsVisible: boolean): NavItem[] {
  const p = session.permissions;
  const items: NavItem[] = [{ to: '/inbox/calls', label: 'Calls', icon: History, match: ['calls'] }];
  if (hasPermission(p, 'contacts:read')) items.push({ to: '/contacts', label: 'Contacts', icon: UserRound, match: ['contacts'] });
  if (hasPermission(p, 'users:read')) items.push({ to: '/team', label: 'Team', icon: Users, match: ['team'] });
  if (hasPermission(p, 'messages:read_own') || hasPermission(p, 'messages:read_all')) items.push({ to: '/messages', label: 'Messages', icon: MessageSquare, match: ['messages'] });
  // No unread count: the API has no cheap count of new voicemail, so the item carries no badge.
  if (voicemailAccess(session).kind === 'allowed') items.push({ to: '/voicemail', label: 'Voicemail', icon: Voicemail, match: ['voicemail'] });
  if (analyticsVisible) items.push({ to: '/analytics', label: 'Analytics', icon: BarChart3, match: ['analytics', 'live_call'] });
  if (['users:roles', 'numbers:read_all', 'tenant:owner'].some(x => hasPermission(p, x))) items.push({ to: '/admin', label: 'Admin', icon: Shield, match: ['admin'], group: 'setup' });
  if (routingAccess(session).kind === 'allowed') items.push({ to: '/admin/routing', label: 'Routing', icon: Workflow, match: ['routing'], group: 'setup' });
  // Phone menus and prompts (migration 016) use the routing permissions.
  if (routingAccess(session).kind === 'allowed') items.push({ to: '/admin/phone-menus', label: 'Phone menus', icon: Grid3x3, match: ['flow'], group: 'setup' });
  if (blocklistAccess(p).read) items.push({ to: '/blocklist', label: 'Blocked numbers', icon: Ban, match: ['blocklist'], group: 'setup' });
  return items;
}

/** Mode-aware navigation: callers see daily destinations only, with nothing administrative. */
export function workspaceNavItems(session: Session, analyticsVisible: boolean): NavItem[] {
  const items = navItems(session, analyticsVisible);
  if (isOperatorMode(session)) return items;
  return items.filter(item => item.group !== 'setup' && item.to !== '/analytics');
}

export function useNav(): NavItem[] {
  const ws = useWorkspace();
  const analytics = gate(capability(ws.config.value, 'analytics.read'));
  const analyticsVisible = analytics.render !== 'hidden' || hasPermission(ws.permissions, 'analytics:read_team');
  return workspaceNavItems(ws.session, analyticsVisible);
}

/** Compact navigation: every authorized destination, including those not in the bottom bar. */
export function MorePage() {
  const items = useNav();
  return (
    <div className="page">
      <h1 className="page-title">More</h1>
      <nav aria-label="All destinations">
        <ul className="more-list">
          {[...items, { to: '/settings', label: 'Settings', icon: Settings, match: ['settings'] }].map(item => (
            <li key={item.to}>
              <Link to={item.to} className="more-link"><Icon icon={item.icon} /> <span>{item.label}</span></Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/**
 * The index: every authorized destination as words, set in the masthead like a newspaper's section
 * line. The current page carries the brass slip (a half disc from the mark). Below the docked width
 * the bottom bar takes over (BottomNav), with the Phone in the middle.
 */
export function Index({ renderSetup }: { renderSetup?: (items: NavItem[]) => ReactNode } = {}) {
  const ws = useWorkspace();
  const { route, search } = useRouter();
  const items = useNav();
  const operator = isOperatorMode(ws.session);
  const work = items.filter(item => item.group !== 'setup');
  const setup = operator ? items.filter(item => item.group === 'setup') : [];
  const onMissedTab = route.name === 'calls' && search.get('tab') === 'missed';
  const onLogTab = route.name === 'calls' && search.get('tab') === 'log';
  let missedCount = 0;
  for (const c of ws.calls.values()) {
    if (c.outcome === 'missed' && c.direction !== 'outbound') missedCount++;
  }
  const missedLabel = missedCount > 0 ? `Missed (${missedCount})` : 'Missed';
  if (!operator) {
    const byLabel = new Map(work.map(item => [item.label, item]));
    const callsItem = byLabel.get('Calls');
    const contactsItem = byLabel.get('Contacts');
    const teamItem = byLabel.get('Team');
    return (
      <nav className="index" aria-label="Primary">
        <ul className="index-list">
          {callsItem ? <li><Link to={callsItem.to} className="index-link" aria-current={callsItem.match.includes(route.name) && !onMissedTab ? 'page' : undefined}>Calls</Link></li> : null}
          {callsItem ? <li><Link to="/inbox/calls?tab=missed" className="index-link" aria-current={onMissedTab ? 'page' : undefined}>{missedLabel}</Link></li> : null}
          {contactsItem ? <li><Link to={contactsItem.to} className="index-link" aria-current={contactsItem.match.includes(route.name) ? 'page' : undefined}>Contacts</Link></li> : null}
          <li><Link to="/phone" className="index-link" aria-current={route.name === 'phone' ? 'page' : undefined}>Phone</Link></li>
          {teamItem ? <li><Link to={teamItem.to} className="index-link" aria-current={teamItem.match.includes(route.name) ? 'page' : undefined}>Team</Link></li> : null}
          <li><Link to="/more" className="index-link" aria-current={route.name === 'more' || route.name === 'settings' ? 'page' : undefined}>More</Link></li>
        </ul>
      </nav>
    );
  }
  return (
    <nav className="index" aria-label="Primary">
      <ul className="index-list">
        {work.map(item => (
          item.label === 'Calls' ? (
            <li key={item.to} style={{ display: 'contents' }}>
              <Link to={item.to} className="index-link" aria-current={item.match.includes(route.name) && !onMissedTab && !onLogTab ? 'page' : undefined}>{item.label}</Link>
              <Link to="/inbox/calls?tab=missed" className="index-link" aria-current={onMissedTab ? 'page' : undefined}>{missedLabel}</Link>
              <Link to="/inbox/calls?tab=log" className="index-link" aria-current={onLogTab ? 'page' : undefined}>Call Log</Link>
            </li>
          ) : (
            <li key={item.to}>
              <Link to={item.to} className="index-link" aria-current={item.match.includes(route.name) ? 'page' : undefined}>{item.label}</Link>
            </li>
          )
        ))}
      </ul>
      {setup.length && renderSetup ? renderSetup(setup) : null}
    </nav>
  );
}

/**
 * The compact index (tablet and phone): daily destinations as a bottom bar with the Phone in the
 * middle, so it is one thumb away. Tablet shows two more destinations than phone; More has the rest.
 */
export function BottomNav() {
  const ws = useWorkspace();
  const { route } = useRouter();
  const operator = isOperatorMode(ws.session);
  const allWork = useNav().filter(item => item.group !== 'setup');
  const work = operator ? allWork.slice(0, 5) : allWork.filter(item => ['Calls', 'Contacts', 'Team'].includes(item.label));
  const link = (item: ReturnType<typeof useNav>[number], i: number) => (
    <Link key={item.to} to={item.to} className={`bottom-link${i >= 3 ? ' bottom-extra' : ''}`} aria-current={item.match.includes(route.name) ? 'page' : undefined}>
      <Icon icon={item.icon} /> <span>{item.label}</span>
    </Link>
  );
  const phoneTone = ws.liveCalls.length > 0 ? 'live' : readinessTone(ws.readiness.state);
  return (
    <nav className="bottom-nav" aria-label="Primary, compact">
      {work.slice(0, 2).map(link)}
      <Link to="/phone" className="bottom-link bottom-phone" aria-current={route.name === 'phone' ? 'page' : undefined}>
        <span className="bottom-phone-icon"><Icon icon={Phone} /><span className={`dot dot-${phoneTone}`} aria-hidden="true" /></span> <span>Phone</span>
      </Link>
      {work.slice(2).map((item, i) => link(item, i + 2))}
      <Link to="/more" className="bottom-link" aria-current={route.name === 'more' || route.name === 'settings' ? 'page' : undefined}><Icon icon={Menu} /> <span>More</span></Link>
    </nav>
  );
}
